import { Tooltip } from 'antd';
import {
  FARM_DIAGNOSTICS_TREND_DECLINING,
  FARM_DIAGNOSTICS_TREND_FACTORS_CAVEAT,
  FARM_DIAGNOSTICS_TREND_FACTORS_HEADING,
  FARM_DIAGNOSTICS_TREND_IMPROVING,
  FARM_DIAGNOSTICS_TREND_LABELS,
  FARM_DIAGNOSTICS_TREND_STABLE,
} from 'constants/uiConstants';
import React from 'react';
import { LuInfo } from 'react-icons/lu';
import type { TrendDirection, TrendFactor } from './computeFarmDiagnosticsMetrics';

const TREND_DOT_CLASS: Record<TrendDirection, string> = {
  [FARM_DIAGNOSTICS_TREND_IMPROVING]: 'bg-success',
  [FARM_DIAGNOSTICS_TREND_STABLE]: 'bg-gray-medium',
  [FARM_DIAGNOSTICS_TREND_DECLINING]: 'bg-danger',
};

const TREND_TEXT_CLASS: Record<TrendDirection, string> = {
  [FARM_DIAGNOSTICS_TREND_IMPROVING]: 'text-success',
  [FARM_DIAGNOSTICS_TREND_STABLE]: 'text-gray-muted',
  [FARM_DIAGNOSTICS_TREND_DECLINING]: 'text-danger',
};

interface TrendBadgeProps {
  trend: TrendDirection | null;
  /** Optional "what changed since last year" breakdown — shown as a tooltip when non-empty. Never claims to fully explain the 3-yr rolling average (see FARM_DIAGNOSTICS_TREND_FACTORS_CAVEAT). */
  factors?: TrendFactor[];
}

const FactorsTooltipContent: React.FC<{ factors: TrendFactor[] }> = ({ factors }) => (
  <div className="flex flex-col gap-1 py-0.5">
    <span className="font-semibold text-white">
      {FARM_DIAGNOSTICS_TREND_FACTORS_HEADING}
    </span>
    {factors.map((f) => (
      <span key={f.label} className="text-gray-bright">
        {f.label}:{' '}
        {f.changePct != null
          ? `${f.changePct > 0 ? '+' : ''}${f.changePct.toFixed(0)}%`
          : '—'}
      </span>
    ))}
    <span className="mt-1 text-gray-muted">{FARM_DIAGNOSTICS_TREND_FACTORS_CAVEAT}</span>
  </div>
);

/** Colored dot + label for a WUE/Financial WUE rolling-average trend — null renders a neutral dash (not enough consecutive years yet). When `factors` is provided and non-empty, wraps the badge in a tooltip explaining what changed since the previous year. */
const TrendBadge: React.FC<TrendBadgeProps> = ({ trend, factors }) => {
  if (!trend) {
    return <span className="text-sm text-gray-muted">—</span>;
  }

  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 text-sm font-semibold ${TREND_TEXT_CLASS[trend]}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${TREND_DOT_CLASS[trend]}`} />
      {FARM_DIAGNOSTICS_TREND_LABELS[trend]}
    </span>
  );

  if (!factors || factors.length === 0) return badge;

  return (
    <Tooltip title={<FactorsTooltipContent factors={factors} />}>
      <span className="inline-flex cursor-help items-center gap-1">
        {badge}
        <LuInfo size={12} className="text-gray-medium" />
      </span>
    </Tooltip>
  );
};

export default TrendBadge;
